import { EndpointOptions } from '@/types/endpoint.options';
import { ProductGroupEntity } from '@/entities/product-group.entity';
import { ProductAnalysisEntity } from '@/entities/product-analysis.entity';
import { ProductAiAnalysisEntity } from '@/entities/product-ai-analysis.entity';
import type { ProductPhotoEntity } from '@/entities/product.entity';
import { withSuppressedErrorToast } from '@/client/get-axios-instance';
import {
    GroupCsvDownloadResult,
    GroupExtractResponse,
    GroupMergePayload,
    GroupRenamePayload,
    GroupRenameResponse,
} from '@/repositories/group-mutation-repository-handlers';
import { MarketplaceGroupRepositoryBase } from '@/repositories/marketplace-group.repository.base';
import { triggerBlobDownload } from '@/repositories/csv-download';

type DownloadProductAnalysisPdfPayload = {
    productName: string;
    analysis: ProductAnalysisEntity;
    selectedPeriodStart: string | null;
    selectedPeriodEnd: string | null;
    timelineAggregationWindowDays: number;
    reviewsTimelinePoints: {
        periodStart: string;
        periodEnd: string;
        reviewsCount: number;
    }[];
};

type DownloadProductsCsvQuery = {
    productIds?: string[];
    selectionSessionId?: string;
    exportOperationId?: string;
};

type EnqueueAiSelectedProductsAnalysisPayload = {
    productIds?: string[];
    operationId?: string;
};

type EnqueueAiSelectedProductsAnalysisResponse = {
    operationId: string;
    startedAt: number;
    total: number;
    limitWarning: string | null;
};

type ProductGroupProductEntity = {
    id: string;
    sku: string;
    platform: string;
    name: string;
    brandName: string | null;
    photos: ProductPhotoEntity[] | null;
};

type ProductGroupDetailsResponse = {
    id: string;
    name: string;
    brandName: string | null;
    products: ProductGroupProductEntity[];
};

type RenameProductGroupResponse = GroupRenameResponse & {
    brandName: string | null;
};

type ExtractProductsFromGroupPayload = {
    productIds: string[];
};

class MarketplaceProductsRepository extends MarketplaceGroupRepositoryBase<
    ProductGroupEntity[],
    ProductGroupDetailsResponse,
    RenameProductGroupResponse,
    ExtractProductsFromGroupPayload,
    GroupExtractResponse,
    DownloadProductsCsvQuery
> {
    constructor() {
        super({
            resourcePath: 'products',
            csvFileName: 'products.csv',
        });
    }

    getProducts(options: EndpointOptions) {
        return this.getList(options);
    }

    mergeProductGroups(data: GroupMergePayload, options?: EndpointOptions) {
        return this.mergeGroups(data, options);
    }

    getProductGroup(groupId: string, options?: EndpointOptions) {
        return this.getGroup(groupId, options);
    }

    renameProductGroup(groupId: string, data: GroupRenamePayload, options?: EndpointOptions) {
        return this.renameGroup(groupId, data, options);
    }

    extractProductsFromGroup(groupId: string, data: ExtractProductsFromGroupPayload, options?: EndpointOptions) {
        return this.extractFromGroup(groupId, data, options);
    }

    getProductAnalysis(productId: string, options?: EndpointOptions) {
        return this.axios.get<ProductAnalysisEntity>(`/${productId}/analysis`, {
            headers: { Authorization: options?.authToken },
            signal: options?.signal,
        });
    }

    getProductAiAnalysis(productId: string, options?: EndpointOptions) {
        return this.axios.get<ProductAiAnalysisEntity>(`/${productId}/ai-analysis`, {
            headers: { Authorization: options?.authToken },
            signal: options?.signal,
        });
    }

    getCombinedProductAnalysis(productIds: string[], options?: EndpointOptions) {
        return this.axios.post<ProductAnalysisEntity>(
            '/analysis',
            { productIds },
            {
                headers: { Authorization: options?.authToken },
                signal: options?.signal,
            },
        );
    }

    async downloadProductAnalysisPdf(payload: DownloadProductAnalysisPdfPayload, options?: EndpointOptions) {
        const response = await this.axios.post<ArrayBuffer>('/analysis/report-pdf', payload, {
            headers: { Authorization: options?.authToken },
            signal: options?.signal,
            responseType: 'arraybuffer',
        });

        const blob = new Blob([response.data], { type: 'application/pdf' });
        triggerBlobDownload(blob, response.headers as Record<string, unknown>, 'product-analysis-report.pdf');
    }

    enqueueAiSelectedProductsAnalysis(data: EnqueueAiSelectedProductsAnalysisPayload, options?: EndpointOptions) {
        return this.axios.post<EnqueueAiSelectedProductsAnalysisResponse>('/ai-analysis-queue', data, {
            ...withSuppressedErrorToast({
                headers: { Authorization: options?.authToken },
                signal: options?.signal,
            }),
        });
    }

    async downloadCsv(query: DownloadProductsCsvQuery, options?: EndpointOptions): Promise<GroupCsvDownloadResult> {
        return super.downloadCsv(query, options);
    }

    getProductSelectionSession(sessionId: string, options?: EndpointOptions) {
        return this.axios.get<{ ids: string[] }>('/selection', {
            params: { sessionId },
            headers: { Authorization: options?.authToken },
            signal: options?.signal,
        });
    }

    setProductSelectionSession(sessionId: string, ids: string[], options?: EndpointOptions) {
        return this.axios.post<{ ids: string[] }>(
            '/selection',
            { sessionId, ids },
            {
                headers: { Authorization: options?.authToken },
                signal: options?.signal,
            },
        );
    }

    updateProductSelectionSession(
        sessionId: string,
        data: {
            addIds?: string[];
            removeIds?: string[];
        },
        options?: EndpointOptions,
    ) {
        return this.axios.patch(
            '/selection',
            { sessionId, ...data },
            {
                headers: { Authorization: options?.authToken },
                signal: options?.signal,
            },
        );
    }

    clearProductSelectionSession(sessionId: string, options?: EndpointOptions) {
        return this.axios.delete('/selection', {
            params: { sessionId },
            headers: { Authorization: options?.authToken },
            signal: options?.signal,
        });
    }
}

export const createMarketplaceProductsRepository = () => new MarketplaceProductsRepository();

export const marketplaceProductsRepository = createMarketplaceProductsRepository();
